/**
 * Prerender readiness helpers.
 *
 * Pages that fetch data client-side must call `beginPrerenderWait()` before
 * their fetch and `endPrerenderWait()` after it completes (both success and
 * error). ScrollToTop only flips `window.__PRERENDER_READY = true` once the
 * pending counter is zero, so Puppeteer captures fully-hydrated HTML instead
 * of loading spinners. This eliminates "false CSR" for data-driven pages
 * (blog list, blog post, CMS pages, resources hub).
 *
 * No-op in non-browser contexts.
 */

type PrerenderWindow = Window & {
  __PRERENDER_PENDING?: number;
  __PRERENDER_READY?: boolean;
};

export function beginPrerenderWait(): void {
  if (typeof window === "undefined") return;
  const w = window as PrerenderWindow;
  w.__PRERENDER_PENDING = (w.__PRERENDER_PENDING ?? 0) + 1;
  // Any in-flight fetch invalidates prior readiness so a snapshot can't slip
  // through between navigation and the first data call.
  w.__PRERENDER_READY = false;
}

export function endPrerenderWait(): void {
  if (typeof window === "undefined") return;
  const w = window as PrerenderWindow;
  w.__PRERENDER_PENDING = Math.max(0, (w.__PRERENDER_PENDING ?? 0) - 1);
}

export function getPrerenderPending(): number {
  if (typeof window === "undefined") return 0;
  return (window as PrerenderWindow).__PRERENDER_PENDING ?? 0;
}
